import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import hyLogo from 'assets/img/logos/hy-logo.png';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '2px solid #2c5282',
    paddingBottom: 15
  },
  logo: {
    width: 80,
    height: 80
  },
  headerText: {
    textAlign: 'right'
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c5282'
  },
  subtitle: {
    fontSize: 12,
    color: '#4a5568',
    marginTop: 5
  },
  section: {
    marginBottom: 15
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    backgroundColor: '#edf2f7',
    padding: 8,
    marginBottom: 10,
    color: '#2d3748'
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5
  },
  label: {
    width: '35%',
    fontWeight: 'bold',
    color: '#4a5568'
  },
  value: {
    width: '65%',
    color: '#1a202c'
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#2c5282',
    padding: 8,
    fontWeight: 'bold',
    color: '#ffffff'
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: '1px solid #e2e8f0'
  },
  tableRowAlt: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f7fafc'
  },
  tableCell: {
    flex: 1
  },
  tableCellWide: {
    flex: 2
  },
  tableCellRight: {
    flex: 1,
    textAlign: 'right'
  },
  totalsContainer: {
    marginTop: 10,
    alignItems: 'flex-end'
  },
  totalsRow: {
    flexDirection: 'row',
    width: 250,
    justifyContent: 'space-between',
    marginBottom: 4,
    paddingVertical: 2
  },
  totalsDivider: {
    width: 250,
    borderBottom: '1px solid #e2e8f0',
    marginVertical: 4
  },
  totalLabel: {
    color: '#4a5568',
    fontWeight: 'bold'
  },
  totalValue: {
    textAlign: 'right',
    color: '#1a202c'
  },
  grandTotalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#2c5282'
  },
  grandTotalValue: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'right',
    color: '#2c5282'
  },
  refundCard: {
    padding: 8,
    backgroundColor: '#fff5f5',
    borderLeft: '3px solid #e53e3e',
    marginBottom: 6
  },
  refundAmount: {
    color: '#e53e3e',
    fontWeight: 'bold'
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTop: '1px solid #e2e8f0',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#718096'
  }
});

const formatMoney = amount => {
  if (amount == null) return '£0.00';
  return `£${Number(amount).toFixed(2)}`;
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatStatus = status => {
  if (!status) return '-';
  return status
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const InvoicePDF = ({ order, donor, organization }) => {
  const donorName =
    [donor?.first_name, donor?.last_name].filter(Boolean).join(' ') || '-';

  // Get email (app-created or migrated format)
  const donorEmail =
    Array.isArray(donor?.email) && donor.email.length > 0
      ? donor.email[0].email
      : donor?.primary_email || '';

  // Get phone (app-created or migrated format)
  const donorPhone =
    Array.isArray(donor?.phone) && donor.phone.length > 0
      ? donor.phone[0].phoneNumber || donor.phone[0].number || ''
      : donor?.primary_phone || '';

  // Get address (app-created array or migrated object format)
  const addressParts = (() => {
    const addr = donor?.address;
    if (Array.isArray(addr) && addr.length > 0) {
      return [
        addr[0].addressLine1,
        addr[0].addressLine2,
        addr[0].city,
        addr[0].postcode,
        addr[0].country
      ].filter(Boolean);
    }
    if (addr && typeof addr === 'object') {
      return [
        addr.street,
        addr.street2,
        addr.city,
        addr.state,
        addr.postcode,
        addr.country
      ].filter(Boolean);
    }
    return donor?.primary_address ? [donor.primary_address] : [];
  })();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src={hyLogo} style={styles.logo} />
          <View style={styles.headerText}>
            <Text style={styles.title}>Receipt</Text>
            {organization?.name && (
              <Text style={styles.subtitle}>{organization.name}</Text>
            )}
          </View>
        </View>

        {/* Invoice Info */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Invoice Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Donation ID:</Text>
            <Text style={styles.value}>{order.simple_id || '-'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Date:</Text>
            <Text style={styles.value}>{formatDate(order.created_at)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Status:</Text>
            <Text style={styles.value}>
              {formatStatus(order.payment_status)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Payment Method:</Text>
            <Text style={styles.value}>
              {order.payment_method?.payment_name || '-'}
            </Text>
          </View>
        </View>

        {/* Donor Info */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Donor Information</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Name:</Text>
            <Text style={styles.value}>{donorName}</Text>
          </View>
          {addressParts.length > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>Address:</Text>
              <Text style={styles.value}>{addressParts.join(', ')}</Text>
            </View>
          )}
        </View>

        {/* Items Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Order Items</Text>
          <View style={styles.tableHeader}>
            <Text style={styles.tableCellWide}>Package Title</Text>
            <Text style={styles.tableCell}>Donation Type</Text>
            <Text style={styles.tableCell}>Billing Condition</Text>
            <Text style={styles.tableCellRight}>Amount</Text>
          </View>
          {order.order_details?.map((item, index) => (
            <View
              key={index}
              style={index % 2 === 1 ? styles.tableRowAlt : styles.tableRow}
            >
              <Text style={styles.tableCellWide}>
                {item.package?.package_title || 'Quick Donation'}
              </Text>
              <Text style={styles.tableCell}>
                {item.donation_type?.donation_type_name || '-'}
              </Text>
              <Text style={styles.tableCell}>
                {item.billing_condition
                  ? item.billing_condition.charAt(0).toUpperCase() +
                    item.billing_condition.slice(1)
                  : '-'}
              </Text>
              <Text style={styles.tableCellRight}>
                {formatMoney(item.amount)}
              </Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalLabel}>Subtotal:</Text>
            <Text style={styles.totalValue}>{formatMoney(order.subtotal)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalLabel}>Transaction Fee:</Text>
            <Text style={styles.totalValue}>
              {formatMoney(order.transaction_fees)}
            </Text>
          </View>
          <View style={styles.totalsDivider} />
          <View style={styles.totalsRow}>
            <Text style={styles.grandTotalLabel}>Total Amount:</Text>
            <Text style={styles.grandTotalValue}>
              {formatMoney(order.donation_amount)}
            </Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalLabel}>Gift Aid:</Text>
            <Text style={styles.totalValue}>
              {order.gift_aid_eligible ? 'Eligible' : 'Not Eligible'}
            </Text>
          </View>
        </View>

        {/* Refunds */}
        {order.refunds?.length > 0 && (
          <View style={[styles.section, { marginTop: 15 }]}>
            <Text style={styles.sectionTitle}>Refund History</Text>
            {order.refunds
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map((refund, index) => (
                <View key={index} style={styles.refundCard}>
                  <View style={styles.row}>
                    <Text style={styles.refundAmount}>
                      -{formatMoney(refund.amount)}
                    </Text>
                    <Text style={{ color: '#718096', marginLeft: 10 }}>
                      {formatDate(refund.created_at)}
                    </Text>
                  </View>
                  {refund.reason && (
                    <Text style={{ color: '#4a5568', marginTop: 2 }}>
                      Reason: {refund.reason}
                    </Text>
                  )}
                  {refund.notes && (
                    <Text style={{ color: '#4a5568', marginTop: 2 }}>
                      Notes: {refund.notes}
                    </Text>
                  )}
                </View>
              ))}
            <View style={[styles.totalsContainer, { marginTop: 6 }]}>
              <View style={styles.totalsRow}>
                <Text style={[styles.totalLabel, { color: '#e53e3e' }]}>
                  Total Refunded:
                </Text>
                <Text style={[styles.totalValue, { color: '#e53e3e' }]}>
                  {formatMoney(
                    order.refunds.reduce((sum, r) => sum + r.amount, 0)
                  )}
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <View>
            <Text>Generated: {new Date().toLocaleString('en-GB')}</Text>
            {organization?.organisation_name && (
              <Text style={{ marginTop: 2 }}>{organization.organisation_name}</Text>
            )}
          </View>
          <View style={{ textAlign: 'right' }}>
            <Text>info@helpyateem.org | +44 (0)207 550 6899</Text>
            <Text style={{ marginTop: 2 }}>
              Hyat Hub, Suite 13, Durning Hall, Earlham Grove, London, E7 9AB
            </Text>
            <Text style={{ marginTop: 2 }}>Charity Number: 1158571</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

InvoicePDF.propTypes = {
  order: PropTypes.object.isRequired,
  donor: PropTypes.object,
  organization: PropTypes.object
};

export default InvoicePDF;
